import type { LocalStore, StoredFile } from "./local";
import { folderSchema, type ProjectFolder } from "./types";

/**
 * Local project files in this browser's IndexedDB: file records by ID, indexed by project, and each version's bytes
 * by key. IndexedDB keeps binary data and far more than localStorage, and stays with this browser profile, as local
 * drawings do.
 */

const DATABASE = "orthos.files";
const DATABASE_VERSION = 1;
const FILES = "files";
const BLOBS = "blobs";
const BY_PROJECT = "project";
/** A project's folders sit beside the blobs under this key prefix, so the database needs no new store. */
const FOLDERS_KEY = "folders:";

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB failed"));
  });
}

function open(): Promise<IDBDatabase> {
  const req = indexedDB.open(DATABASE, DATABASE_VERSION);
  req.onupgradeneeded = () => {
    const db = req.result;
    const files = db.createObjectStore(FILES, { keyPath: "id" });
    files.createIndex(BY_PROJECT, "projectId");
    db.createObjectStore(BLOBS);
  };
  return request(req);
}

function isStoredFile(value: unknown): value is StoredFile {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    "projectId" in value &&
    "versions" in value &&
    Array.isArray(value.versions)
  );
}

/** The browser's store, opened on first use. */
export function indexedDbStore(): LocalStore {
  let database: Promise<IDBDatabase> | undefined;
  const db = (): Promise<IDBDatabase> => (database ??= open());
  const store = async (name: string, mode: IDBTransactionMode): Promise<IDBObjectStore> =>
    (await db()).transaction(name, mode).objectStore(name);

  return {
    async files(projectId) {
      const index = (await store(FILES, "readonly")).index(BY_PROJECT);
      const rows: unknown[] = await request(index.getAll(projectId));
      return rows.filter(isStoredFile);
    },
    async putFile(file) {
      await request((await store(FILES, "readwrite")).put(file));
    },
    async blob(key) {
      const value: unknown = await request((await store(BLOBS, "readonly")).get(key));
      return value instanceof Uint8Array ? value : undefined;
    },
    async putBlob(key, bytes) {
      await request((await store(BLOBS, "readwrite")).put(bytes, key));
    },
    async folders(projectId) {
      const value: unknown = await request(
        (await store(BLOBS, "readonly")).get(`${FOLDERS_KEY}${projectId}`),
      );
      const parsed = folderSchema.array().safeParse(value);
      return parsed.success ? parsed.data : [];
    },
    async putFolders(projectId, folders: readonly ProjectFolder[]) {
      await request(
        (await store(BLOBS, "readwrite")).put([...folders], `${FOLDERS_KEY}${projectId}`),
      );
    },
    async deleteFile(id) {
      await request((await store(FILES, "readwrite")).delete(id));
    },
    async deleteBlob(key) {
      await request((await store(BLOBS, "readwrite")).delete(key));
    },
  };
}
